import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import PropTypes from 'prop-types';

import { Column } from 'primereact/column';
import ButtonSp from '../../components/ButtonSp';

import InputTextSp from '../../components/InputTextSp';
import LabelSp from '../../components/LabelSp';
import { showMessage } from '../../components/MessageDialog';

import SistemaService from '../../services/SistemaService';

import { errorHandle, formatFloat, validateFields } from '../../util/functions';
import { SistemaEdicaoModel, SistemaModel } from '../../util/Models';
import { StateScreen } from '../constants';
import DataTableSp from '../../components/DataTableSp';
import BotaoMenuGrid from '../../components/BotaoMenuGrid';

import EdicaoSistema from './edicaoSistema';

function SistemaCrud(props) {
  SistemaCrud.propTypes = {
    stateScreen: PropTypes.string.isRequired,
    idSelected: PropTypes.number.isRequired,
    onClose: PropTypes.func.isRequired,
  };

  SistemaCrud.defaultProps = {
    messageConfirmation: 'Confirma os dados',
    showConfirmation: false,
  };

  const { stateScreen, idSelected, onClose } = props;

  // states
  const [sistema, setSistema] = useState(new SistemaModel());

  const [errorLoadRecord, setErrorLoadRecord] = useState(false);
  const [editandoEdicao, setEditandoEdicao] = useState(false);
  const [visualizandoEdicao, setVisualizandoEdicao] = useState(false);
  const [showEdicao, setShowEdicao] = useState(false);
  const [edicaoSelecionada, setEdicaoSelecionada] = useState();

  // useCallbacks

  const loadRecord = useCallback(async _id => {
    try {
      const retorno = await SistemaService.findById(_id);

      setSistema(retorno);

      setErrorLoadRecord(false);
    } catch (err) {
      setErrorLoadRecord(true);
      errorHandle(err);
    }
  }, []);

  // funcoes
  function viewMode() {
    return stateScreen === StateScreen.stView || errorLoadRecord;
  }

  const excluirEdicao = useCallback(
    rowData => {
      const idx = sistema.sistemaEdicao.findIndex(e => e === rowData);
      if (idx >= 0) {
        sistema.sistemaEdicao.splice(idx, 1);
        setSistema({
          ...sistema,
          sistemaEdicao: [...sistema.sistemaEdicao],
        });
      }
    },
    [sistema]
  );

  const salvaEdicao = useCallback(
    edicao => {
      const idx = sistema.sistemaEdicao.findIndex(e => e.idEdicao === edicao.idEdicao);
      if (idx >= 0) {
        sistema.sistemaEdicao[idx] = edicao;
      } else {
        sistema.sistemaEdicao.push(edicao);
      }
      setSistema({ ...sistema, sistemaEdicao: [...sistema.sistemaEdicao] });
    },
    [sistema]
  );

  function handleBack() {
    if (stateScreen === StateScreen.stView) {
      onClose();
    } else {
      showMessage('Confirmação', 'Abandonar mudanças?', idx => {
        if (idx === 1) {
          onClose();
        }
      });
    }
  }

  function handleSave() {
    if (stateScreen === StateScreen.stView) {
      onClose();
    } else {
      salvarRecord();
    }
  }

  async function salvarRecord() {
    try {
      let retorno;
      if (stateScreen === StateScreen.stInsert) {
        retorno = await SistemaService.insert(sistema);
      } else {
        retorno = await SistemaService.update(sistema);
      }
      toast.success('Registro salvo com sucesso.');
      onClose(retorno);
    } catch (err) {
      errorHandle(err);
    }
  }

  // useEffects
  useEffect(() => {
    if (stateScreen === StateScreen.stUpdate || stateScreen === StateScreen.stView) {
      loadRecord(idSelected);
    } else if (stateScreen === StateScreen.stInsert) {
      const novo = new SistemaModel();

      setSistema(novo);
    }
  }, [loadRecord, idSelected, stateScreen]);

  // render principal
  return (
    <>
      {showEdicao ? (
        <EdicaoSistema
          visible={showEdicao}
          viewMode={visualizandoEdicao}
          sistemaEdicao={edicaoSelecionada}
          modoEdicao={editandoEdicao}
          handleConfirm={reg => {
            salvaEdicao(reg);
            setShowEdicao(false);
          }}
          handleClose={() => {
            setShowEdicao(false);
            setEdicaoSelecionada(undefined);
          }}
        />
      ) : null}

      <div className="p-col-12 p-sm-6 p-lg-6 p-fluid">
        <LabelSp>Nome</LabelSp>
        <InputTextSp
          autoFocus
          value={sistema.nome}
          maxLength={100}
          required
          disabled={viewMode()}
          onChange={e => {
            setSistema({ ...sistema, nome: e.target.value });
          }}
        />
      </div>
      <div className="p-col-12 p-sm-6 p-lg-6 p-fluid">
        <LabelSp>Descrição</LabelSp>
        <InputTextSp
          value={sistema.descricao}
          maxLength={100}
          disabled={viewMode()}
          onChange={e => {
            setSistema({ ...sistema, descricao: e.target.value });
          }}
        />
      </div>

      <div className="p-col-12 p-lg-12" style={{ textAlign: 'start' }}>
        <ButtonSp
          className="p-button-primary"
          label="Inserir Edição"
          icon="pi pi-plus-circle"
          disabled={viewMode()}
          onClick={() => {
            setEditandoEdicao(false);
            setEdicaoSelecionada(new SistemaEdicaoModel());
            setShowEdicao(true);
            setVisualizandoEdicao(false);
          }}
        />
      </div>

      <div className="p-col-12 p-fluid">
        <DataTableSp value={sistema.sistemaEdicao} style={{ marginBottom: '2px' }} responsive>
          <Column
            className="grid-col"
            header="Edição"
            body={rowData => rowData.edicao?.nome}
          />
          <Column
            className="grid-col-val"
            header="% Salário"
            body={rowData => formatFloat(rowData.percSalario, 3)}
          />

          <Column
            className="grid-col-curr"
            header="% Acesso Ad."
            body={rowData => formatFloat(rowData.percAcessoAdicional, 3)}
          />
          <Column
            className="grid-col-curr"
            header="Qtd. Acesso Inc."
            body={rowData => formatFloat(rowData.qtdAcessoIncluso, 0)}
          />

          <Column
            className="gid-col-acoes-35"
            bodyStyle={{ textAlign: 'end' }}
            body={renderButtonOpEdicao}
          />
        </DataTableSp>
      </div>

      <div className="p-col-12 p-lg-12" style={{ textAlign: 'start' }}>
        {!viewMode() ? (
          <ButtonSp
            className="p-button-success"
            icon="pi pi-save"
            label="Salvar"
            keyAccess="S"
            disabled={!validateFields(sistema, ['nome'])}
            showConfirmation
            onClick={handleSave}
          />
        ) : null}
        <ButtonSp
          className="p-button-secondary"
          label="Voltar"
          icon="pi pi-chevron-circle-left"
          keyAccess="V"
          onClick={handleBack}
        />
      </div>
    </>
  );

  function renderButtonOpEdicao(rowData) {
    return (
      <>
        <BotaoMenuGrid
          // disabled={viewMode()}
          handles={[
            () => {
              setEditandoEdicao(true);
              setVisualizandoEdicao(false);
              setEdicaoSelecionada(rowData);
              setShowEdicao(true);
            },
            () => {
              excluirEdicao(rowData);
            },
            () => {
              setVisualizandoEdicao(true);
              setEdicaoSelecionada(rowData);
              setShowEdicao(true);
            },
          ]}
          disableds={[viewMode(), viewMode(), false]}
          labels={['Alterar', 'Excluir', 'Visualizar']}
          icons={['pi pi-pencil', 'pi pi-trash', 'pi pi-search']}
        />
      </>
    );
  }
}

export default SistemaCrud;
